/**
 * "How it was built" on the public share page: the chat sessions that built
 * the app (the IDE's own conversation and any histories imported from other
 * coding agents), and a reader for the selected one.
 *
 * Shown only when the owner has made the transcripts public; the server 404s
 * otherwise. The open session is in the URL (`?session=<id>`) so it can be
 * linked.
 *
 * @module
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button } from '@molecule/app-ui-react'

import {
  pick,
  type PublicSession,
  SESSION_PARAM,
  type SessionTurn,
  SHARE_SLUG_HEADER,
  statusOf,
  type TranscriptSummary,
} from '../../lib/public-build.js'

/** A turn's text longer than this is collapsed behind "Show more". */
const COLLAPSE_CHARS = 1200

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; sessions: TranscriptSummary[] }
  | { kind: 'hidden' }
  | { kind: 'error' }

type SessionState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; session: PublicSession }
  | { kind: 'error' }

/**
 * A date in the reader's locale, or nothing for an unparseable value.
 *
 * @param iso - An ISO 8601 time.
 * @returns The formatted date, or ''.
 */
function formatDate(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * The share page's session list and reader.
 *
 * @param props - The share slug.
 * @param props.slug - The share link's slug (the read credential).
 * @returns The section, or nothing when the transcripts are not public.
 */
export function BuildSessions({ slug }: { slug: string }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // Read through a ref so the fetches depend on what they fetch, not on the
  // client object's identity.
  const httpRef = useRef(http)
  httpRef.current = http
  const [params, setParams] = useSearchParams()
  const selected = params.get(SESSION_PARAM)
  const [list, setList] = useState<ListState>({ kind: 'loading' })
  const [open, setOpen] = useState<SessionState>({ kind: 'idle' })
  const headers = useMemo(() => ({ [SHARE_SLUG_HEADER]: slug }), [slug])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await httpRef.current.get<unknown>(
          `/share/${encodeURIComponent(slug)}/sessions`,
          {
            headers,
          },
        )
        if (cancelled) return
        setList({ kind: 'ready', sessions: pick<TranscriptSummary[]>(res.data, 'sessions') ?? [] })
      } catch (error) {
        if (cancelled) return
        setList({ kind: statusOf(error) === 404 ? 'hidden' : 'error' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug, headers])

  // The URL names the open session; without one, the first session opens.
  const sessionId =
    list.kind === 'ready'
      ? (list.sessions.find((s) => s.id === selected)?.id ?? list.sessions[0]?.id ?? null)
      : null

  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    setOpen({ kind: 'loading' })
    void (async () => {
      try {
        const res = await httpRef.current.get<unknown>(
          `/share/${encodeURIComponent(slug)}/sessions/${encodeURIComponent(sessionId)}`,
          { headers },
        )
        if (cancelled) return
        const session = pick<PublicSession>(res.data, 'session')
        setOpen(session ? { kind: 'ready', session } : { kind: 'error' })
      } catch (_error) {
        // The list already loaded, so the link is valid; one session failing to
        // load is shown in place of the reader.
        if (!cancelled) setOpen({ kind: 'error' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug, sessionId, headers])

  if (list.kind === 'hidden') return null

  const choose = (id: string): void => {
    const next = new URLSearchParams(params)
    next.set(SESSION_PARAM, id)
    setParams(next, { replace: false, preventScrollReset: true })
  }

  return (
    <section data-mol-id="shared-build-sessions" style={{ marginTop: 40 }}>
      <h2 className={cm.cn(cm.textSize('xl'), cm.fontWeight('bold'))} style={{ margin: 0 }}>
        {t('share.build.title', undefined, { defaultValue: 'How it was built' })}
      </h2>
      <p className={cm.textMuted} style={{ margin: '6px 0 16px', lineHeight: 1.5 }}>
        {t('share.build.lede', undefined, {
          defaultValue: 'The chat sessions with the AI agents that built this app.',
        })}
      </p>

      {list.kind === 'loading' && (
        <p className={cm.textMuted} data-mol-id="shared-build-sessions-loading">
          {t('share.build.loading', undefined, { defaultValue: 'Loading the sessions…' })}
        </p>
      )}
      {list.kind === 'error' && (
        <p className={cm.textError} data-mol-id="shared-build-sessions-error">
          {t('share.build.error', undefined, { defaultValue: 'The sessions could not be loaded.' })}
        </p>
      )}
      {list.kind === 'ready' && list.sessions.length === 0 && (
        <p className={cm.textMuted} data-mol-id="shared-build-sessions-empty">
          {t('share.build.empty', undefined, { defaultValue: 'No sessions have been shared yet.' })}
        </p>
      )}

      {list.kind === 'ready' && list.sessions.length > 0 && (
        <div className="mol-build-layout">
          <ul className="mol-build-list" data-mol-id="shared-build-session-list">
            {list.sessions.map((s) => {
              const active = s.id === sessionId
              const meta = [
                s.harness,
                s.model,
                formatDate(s.startedAt ?? s.importedAt),
                t('share.build.turns', { count: s.turns }, { defaultValue: '{{count}} turns' }),
              ].filter(Boolean)
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => choose(s.id)}
                    aria-current={active ? 'true' : undefined}
                    className={cm.cn(cm.resultItem, active && cm.resultItemActive, cm.w('full'))}
                    style={{ textAlign: 'left' }}
                    data-mol-id="shared-build-session"
                    data-session-id={s.id}
                  >
                    {/* One block, so the title and its details stack instead of
                        sitting side by side in the row's flex layout. */}
                    <span style={{ display: 'block', minWidth: 0 }}>
                      <span className={cm.resultTitle} style={{ display: 'block' }}>
                        {s.title ||
                          t('share.build.untitled', undefined, {
                            defaultValue: 'Untitled session',
                          })}
                      </span>
                      <span className={cm.resultMeta} style={{ display: 'block', marginTop: 2 }}>
                        {meta.join(' · ')}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>

          <div className="mol-build-reader" data-mol-id="shared-build-reader">
            {open.kind === 'loading' && (
              <p className={cm.textMuted}>
                {t('share.build.sessionLoading', undefined, { defaultValue: 'Loading…' })}
              </p>
            )}
            {open.kind === 'error' && (
              <p className={cm.textError}>
                {t('share.build.sessionError', undefined, {
                  defaultValue: 'This session could not be loaded.',
                })}
              </p>
            )}
            {open.kind === 'ready' && <SessionReader session={open.session} />}
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * One session's turns: the person's messages and the agent's replies, each
 * reply with its model and the files it wrote.
 *
 * @param props - The session.
 * @param props.session - The session to show.
 * @returns The reader.
 */
export function SessionReader({ session }: { session: PublicSession }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  if (session.turns.length === 0) {
    return (
      <p className={cm.textMuted}>
        {t('share.build.noTurns', undefined, { defaultValue: 'This session has no messages.' })}
      </p>
    )
  }
  return (
    <ol className="mol-build-turns" data-mol-id="shared-build-turns">
      {session.turns.map((turn, i) => (
        <TurnView key={i} turn={turn} fallbackModel={session.model} harness={session.harness} />
      ))}
    </ol>
  )
}

/**
 * One turn. The person's messages and the agent's replies are told apart by
 * the accent bar's color and the label above the text.
 *
 * @param props - The turn.
 * @param props.turn - The turn.
 * @param props.fallbackModel - The session's model, when the turn names none.
 * @param props.harness - The harness, for the agent label.
 * @returns The turn.
 */
function TurnView({
  turn,
  fallbackModel,
  harness,
}: {
  turn: SessionTurn
  fallbackModel?: string
  harness: string
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const long = turn.text.length > COLLAPSE_CHARS
  const text = long && !expanded ? `${turn.text.slice(0, COLLAPSE_CHARS).trimEnd()}…` : turn.text
  const isUser = turn.role === 'user'
  const tone = isUser ? 'info' : 'primary'
  const model = turn.model ?? (isUser ? undefined : fallbackModel)
  const label = isUser
    ? t('share.build.person', undefined, { defaultValue: 'Person' })
    : model
      ? `${harness} · ${model}`
      : harness
  return (
    <li
      className={cm.cn(cm.accentCard({ tone }), 'mol-build-turn')}
      data-mol-id="shared-build-turn"
      data-role={turn.role}
    >
      <span className={cm.accentCardLabel({ tone })}>{label}</span>
      {text && <div className="mol-build-text">{text}</div>}
      {long && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          data-mol-id="shared-build-turn-more"
        >
          {expanded
            ? t('share.build.showLess', undefined, { defaultValue: 'Show less' })
            : t('share.build.showMore', undefined, { defaultValue: 'Show more' })}
        </Button>
      )}
      {turn.files.length > 0 && (
        <div className="mol-build-files" data-mol-id="shared-build-turn-files">
          {turn.files.map((f, i) => (
            <details key={`${f.path}-${i}`} className="mol-build-file">
              <summary>
                <code>{f.path}</code>{' '}
                <span className={cm.textMuted}>
                  {f.kind === 'create'
                    ? t('share.build.fileCreated', undefined, { defaultValue: 'written' })
                    : t('share.build.fileEdited', undefined, { defaultValue: 'edited' })}
                  {!f.complete &&
                    ` · ${t('share.build.filePartial', undefined, { defaultValue: 'partial' })}`}
                </span>
              </summary>
              {f.text && <pre className={cm.surface}>{f.text}</pre>}
            </details>
          ))}
        </div>
      )}
    </li>
  )
}
